import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions';
import supabase from '../../lib/supabaseClient.js';
import TenantBehaviorSettings from '../TenantBehaviorSettings.jsx';
import CampaignDashboard from '../settings/aiconfigurationhub/CampaignDashboard.jsx';
import StrategyBuilder from '../settings/aiconfigurationhub/StrategyBuilder';
import KnowledgeBase from './Knowledgebase.jsx';
import {
  Wand2,
  Settings,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  RefreshCw,
  FileText,
  AlertCircle,
} from 'lucide-react';
import AutomationRules from './aiconfigurationhub/AutomationRules';
import {
  toneOptions,
  initialPersonaOptions,
  engagementPersonaOptions,
  followupPersonaOptions,
  strategyTemplates
} from './aiconfigurationhub/constants';
import {
  buildInstructionBundle,
  buildInitialInstruction,
  buildFollowupInstruction  
} from '../../lib/instructionBuilder.js';
import InstructionRebuildTool from '../InstructionRebuildTool';

const AIConfigurationHub = () => {
  const { user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('campaign-builder');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Permission checks
  const canViewAISettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS) || hasPermission(PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW);
  const canEditAISettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);
  const canEditInstructions = hasPermission(PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS);
  const canRebuildBundle = hasPermission(PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE);
  const canViewBundlePreview = hasPermission(PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW);
  const canViewKnowledgeBase = hasPermission(PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW) || hasPermission(PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS);
  const canUploadDocs = hasPermission(PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS);
  const canDeleteDocs = hasPermission(PERMISSIONS.DELETE_KB_DOCS);

  // Campaign Builder State
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyIndustry, setCompanyIndustry] = useState('');
  const [strategyConfig, setStrategyConfig] = useState({
    businessName: '',
    initialTone: '',
    initialPersona: '',
    engagementTone: '',
    engagementPersona: '',
    requireQualification: false,
    qualificationQuestions: [], 
    followups: [
      {
        id: 1,
        day: 3,
        type: 'gentle_reminder',
        tone: '',
        persona: '',
        enabled: true
      },
      {
        id: 2,
        day: 7,
        type: 'value_add',
        tone: '',
        persona: '',
        enabled: true
      },
      {
        id: 3,
        day: 14,
        type: 'final_attempt',
        tone: '',
        persona: '',
        enabled: true
      }
    ]
  });
  const [campaigns, setCampaigns] = useState([]);

// Global Admin State
  const isGlobalAdmin = user?.role === 'global_admin';
  const [allTenants, setAllTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);

  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:3001';
    }
    return 'https://api.getsurfox.com/api';
  };

  const callAPI = async (endpoint, options = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const apiBaseUrl = getApiBaseUrl();
      const fullUrl = `${apiBaseUrl}${endpoint}`;

      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error(`API endpoint not found. Expected JSON but received HTML. Check your API URL: ${fullUrl}`);
        }

        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: `Server error (${response.status}): ${errorText}` };
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      
      if (!responseText) {
        return {};
      }

      try {
        return JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Invalid JSON response from API: ${responseText.substring(0, 200)}...`);
      }
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };

  // Load Campaign-Specific configuration when campaign is selected
useEffect(() => {
  const loadCampaignConfiguration = async () => {
    if (!selectedCampaign?.id || !canViewAISettings) {
      setLoading(false);
      return;
    }

    try {
      // Get campaign-specific AI settings
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select(`
          initial_tone,
          initial_persona,
          engagement_tone,
          engagement_persona,
          followup_delay_1,
          followup_delay_2,
          followup_delay_3,
          followup_1_tone,
          followup_1_persona,
          followup_2_tone,
          followup_2_persona,
          followup_3_tone,
          followup_3_persona,
          ai_representative_name,
          require_qualification_before_transfer,
          qualification_questions
        `)
        .eq('id', selectedCampaign.id)
        .single();

      if (campaignError) throw campaignError;

      // Get tenant industry for context
      const { data: industryData } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('tenant_id', user.tenant_id)
        .eq('key', 'industry')
        .single();

      setCompanyIndustry(industryData?.value || '');

      // Populate the form with campaign-specific data
      setStrategyConfig({
        businessName: campaignData.ai_representative_name || 'Jack',
        initialTone: campaignData.initial_tone || '',
        initialPersona: campaignData.initial_persona || '',
        engagementTone: campaignData.engagement_tone || '',
        engagementPersona: campaignData.engagement_persona || '',
        requireQualification: campaignData.require_qualification_before_transfer || false,
        qualificationQuestions: campaignData.qualification_questions || [],
        followups: [
          {
            id: 1,
            day: campaignData.followup_delay_1 || 3,
            type: 'gentle_reminder',
            tone: campaignData.followup_1_tone || '',
            persona: campaignData.followup_1_persona || '',
            enabled: true
          },
          {
            id: 2,
            day: campaignData.followup_delay_2 || 7,
            type: 'value_add',
            tone: campaignData.followup_2_tone || '',
            persona: campaignData.followup_2_persona || '',
            enabled: true
          },
          {
            id: 3,
            day: campaignData.followup_delay_3 || 14,
            type: 'final_attempt',
            tone: campaignData.followup_3_tone || '',
            persona: campaignData.followup_3_persona || '',
            enabled: true
          }
        ]
      });

    } catch (err) {
      console.error('Error loading campaign configuration:', err);
      setError(`Failed to load campaign configuration: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (selectedCampaign) {
    loadCampaignConfiguration();
  } else {
    setLoading(false);
  }
}, [selectedCampaign?.id, user?.tenant_id, canViewAISettings]);

  // Load campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user?.tenant_id || !canViewAISettings) return;
      
      try {
        const campaignData = await callAPI(`/campaigns?tenant_id=${user.tenant_id}`);
        setCampaigns(campaignData || []);
      } catch (err) {
        console.error('Error loading campaigns:', err);
        setError(`Failed to load campaigns: ${err.message}`);
      }
    };

    fetchCampaigns();
  }, [user?.tenant_id, canViewAISettings]);

  
  const handleChange = (key, value) => {
    if (!canEditInstructions) {
      setError("You don't have permission to modify AI instructions.");
      return;
    }
    setStrategyConfig(prev => ({ ...prev, [key]: value }));
    setUnsavedChanges(true);
  };

  const handleFollowupChange = (index, field, value) => {
    if (!canEditInstructions) {
      setError("You don't have permission to modify AI instructions.");
      return;
    }
    setStrategyConfig(prev => ({
      ...prev,
      followups: prev.followups.map((followup, i) => 
        i === index ? { ...followup, [field]: value } : followup
      )
    }));
    setUnsavedChanges(true);
  };

  const addQualificationQuestion = useCallback(() => {
  setStrategyConfig(prev => ({
    ...prev,
    qualificationQuestions: [...prev.qualificationQuestions, '']
  }));
  setUnsavedChanges(true);
}, []);

const updateQualificationQuestion = useCallback((index, value) => {
  setStrategyConfig(prev => {
    const updated = [...prev.qualificationQuestions];
    updated[index] = value;
    return { ...prev, qualificationQuestions: updated };
  });
  setUnsavedChanges(true);
}, []);

const removeQualificationQuestion = useCallback((index) => {
  setStrategyConfig(prev => ({
    ...prev,
    qualificationQuestions: prev.qualificationQuestions.filter((_, i) => i !== index)
  }));
  setUnsavedChanges(true);
}, []);

  const applyTemplate = (template) => {
    if (!canEditInstructions) {
      setError("You don't have permission to modify AI instructions.");
      return;
    }

    setStrategyConfig(prev => ({
      ...prev,
      initialTone: template.tone,
      initialPersona: initialPersonaOptions[0],
      engagementTone: template.tone,
      engagementPersona: template.persona,
      followups: prev.followups.map((followup, index) => ({
        ...followup,
        tone: template.tone,
        persona: followupPersonaOptions[index] || 'Gentle Reminder'
      }))
    }));
    setUnsavedChanges(true);
  };

  const saveStrategy = async () => {
  if (!canRebuildBundle) {
    setError("You don't have permission to rebuild AI instruction bundles.");
    return;
  }

  if (!selectedCampaign) {
    setError("Please select a campaign first");
    return;
  }

  setSaving(true);
  setError('');
  setSuccess('');

  try {
    const campaignMetadata = {
      campaign_id: selectedCampaign.id,
      campaign_name: selectedCampaign.name,
      talk_track_type: selectedCampaign.talk_track_type,
      talk_track_specialty: selectedCampaign.talk_track_specialty
    };

// Fetch tenant behavior settings
let tenantBehaviors = {};
try {
  const { data: behaviorSettings, error: behaviorError } = await supabase
    .from('tenant_behavior_settings')
    .select('*')
    .eq('tenant_id', user.tenant_id)
    .single();
  
  if (!behaviorError && behaviorSettings) {
    tenantBehaviors = {
      push_back_on_not_interested: behaviorSettings.push_back_on_not_interested,
      challenge_budget_objections: behaviorSettings.challenge_budget_objections,
      gate_information: behaviorSettings.gate_information
    };
    console.log('✅ Tenant behavior settings loaded:', tenantBehaviors);
  } else {
    console.log('ℹ️ No tenant behavior settings found, using defaults');
  }
} catch (err) {
  console.error('⚠️ Error fetching tenant behaviors:', err);
  // Continue with empty behaviors if fetch fails
}

    // Build instruction bundles
const initialBundle = buildInitialInstruction({
  tone: strategyConfig.initialTone,
  persona: strategyConfig.initialPersona,
  industry: companyIndustry,
  role: '',
  leadDetails: {},
  knowledgeBase: strategyConfig.knowledgeBase || '',
  campaignMetadata: campaignMetadata,
  platformSettings: { 
    ai_representative_name: { 
      value: selectedCampaign.ai_representative_name || strategyConfig.businessName || 'Assistant' 
    } 
  },
  tenantBehaviors: tenantBehaviors 
});

const engagementBundle = buildInstructionBundle({
  tone: strategyConfig.engagementTone,
  persona: strategyConfig.engagementPersona,
  industry: companyIndustry,
  role: '',
  leadDetails: {},
  knowledgeBase: strategyConfig.knowledgeBase || '',
  campaignMetadata: campaignMetadata,
  platformSettings: { 
    ai_representative_name: { 
      value: selectedCampaign.ai_representative_name || strategyConfig.businessName || 'Assistant' 
    } 
  },
  tenantBehaviors: tenantBehaviors,
  requireQualification: strategyConfig.requireQualification,
  qualificationQuestions: strategyConfig.qualificationQuestions
});

const followupBundles = strategyConfig.followups.map((followup, index) => 
  buildFollowupInstruction({
    tone: followup.tone || strategyConfig.engagementTone,
    persona: followup.persona,
    industry: companyIndustry,
    role: '',
    leadDetails: {},
    knowledgeBase: strategyConfig.knowledgeBase || '',
    campaignMetadata: campaignMetadata,
    followupStage: index + 1,
    platformSettings: { 
      ai_representative_name: { 
        value: selectedCampaign.ai_representative_name || strategyConfig.businessName || 'Assistant' 
      } 
    },
    tenantBehaviors: tenantBehaviors  
  })
);

    // Save to campaigns table
    const { error: updateError } = await supabase
      .from('campaigns')
      .update({
        // Store the built instruction bundles
        ai_instruction_initial: initialBundle,
        ai_instruction_engagement: engagementBundle,
        ai_instruction_followup_1: followupBundles[0] || null,
        ai_instruction_followup_2: followupBundles[1] || null,
        ai_instruction_followup_3: followupBundles[2] || null,
        
        // Store the raw configuration for future editing
        initial_tone: strategyConfig.initialTone,
        initial_persona: strategyConfig.initialPersona,
        engagement_tone: strategyConfig.engagementTone,
        engagement_persona: strategyConfig.engagementPersona,

        require_qualification_before_transfer: strategyConfig.requireQualification,
        qualification_questions: strategyConfig.qualificationQuestions,

        followup_delay_1: strategyConfig.followups[0]?.day || 3,
        followup_delay_2: strategyConfig.followups[1]?.day || 7,
        followup_delay_3: strategyConfig.followups[2]?.day || 14,
        
        followup_1_tone: strategyConfig.followups[0]?.tone || null,
        followup_1_persona: strategyConfig.followups[0]?.persona || null,
        followup_2_tone: strategyConfig.followups[1]?.tone || null,
        followup_2_persona: strategyConfig.followups[1]?.persona || null,
        followup_3_tone: strategyConfig.followups[2]?.tone || null,
        followup_3_persona: strategyConfig.followups[2]?.persona || null
      })
      .eq('id', selectedCampaign.id);

    if (updateError) throw updateError;

    // Update local campaigns state
    setCampaigns(prevCampaigns => 
      prevCampaigns.map(c => 
        c.id === selectedCampaign.id 
          ? { 
              ...c, 
              initial_tone: strategyConfig.initialTone,
              engagement_tone: strategyConfig.engagementTone,
            } 
          : c
      )
    );

    setSuccess(`AI strategy saved for "${selectedCampaign.name}"!`);
    setUnsavedChanges(false);

  } catch (err) {
    console.error('Failed to save strategy:', err);
    setError(`Save failed: ${err.message}`);
  } finally {
    setSaving(false);
  }
};

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canViewAISettings && !canViewKnowledgeBase) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-gray-600">You don't have permission to view AI configuration settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-red-800">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-green-800 font-medium">{success}</span>
          </div>
        )}

        {selectedCampaign && activeTab === 'campaign-builder' && (
          <div className="mb-6">
            <nav className="flex items-center space-x-2 text-sm text-gray-500">
              <button
                onClick={() => {
                  setActiveTab('campaign-builder');
                  setSelectedCampaign(null);
                }}
                className="hover:text-gray-700"
              >
                All Campaigns
              </button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900 font-medium">{selectedCampaign.name}</span>
            </nav>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-200 mb-6 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('campaign-builder')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === 'campaign-builder'
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Wand2 className="w-4 h-4" />
                <span>Campaign Builder</span>
              </button>
              <button
                onClick={() => setActiveTab('automation-rules')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === 'automation-rules'
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>Automation Rules</span>
              </button>
              <button
                onClick={() => setActiveTab('knowledge-base')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === 'knowledge-base'
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Knowledge Base</span>
              </button>
              <button
                onClick={() => setActiveTab('behavior-settings')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === 'behavior-settings'
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>AI Behavior</span>
              </button>
              {isGlobalAdmin && (
                <button
                  onClick={() => setActiveTab('global-rebuild')}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                    activeTab === 'global-rebuild'
                      ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Global Rebuild</span>
                </button>
              )}
            </nav>
          </div>
        </div>

{activeTab === 'campaign-builder' && (selectedCampaign ? 
  <StrategyBuilder 
       selectedCampaign={selectedCampaign}
       strategyConfig={strategyConfig}
       unsavedChanges={unsavedChanges}
       canEditInstructions={canEditInstructions}
       canRebuildBundle={canRebuildBundle}
       isGlobalAdmin={isGlobalAdmin}
       setActiveTab={setActiveTab}
       saving={saving}
       saveStrategy={saveStrategy}
       canViewAISettings={canViewAISettings}
       handleChange={handleChange}
       toneOptions={toneOptions}
       initialPersonaOptions={initialPersonaOptions}
       engagementPersonaOptions={engagementPersonaOptions}
       followupPersonaOptions={followupPersonaOptions}
       strategyTemplates={strategyTemplates}
       applyTemplate={applyTemplate}
       addQualificationQuestion={addQualificationQuestion}
       updateQualificationQuestion={updateQualificationQuestion}
       removeQualificationQuestion={removeQualificationQuestion}
       handleFollowupChange={handleFollowupChange}
     /> : 
          <CampaignDashboard 
            campaigns={campaigns}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            canEditAISettings={canEditAISettings}
            canViewAISettings={canViewAISettings}
            canEditInstructions={canEditInstructions}
            setSelectedCampaign={setSelectedCampaign}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'automation-rules' && <AutomationRules />}
        {activeTab === 'knowledge-base' && <KnowledgeBase />}
        {activeTab === 'behavior-settings' && <TenantBehaviorSettings />}
        {activeTab === 'global-rebuild' && isGlobalAdmin && <InstructionRebuildTool />}
      </div>
    </div>
  );
};

export default AIConfigurationHub;